import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Divider,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getLocations } from '../../organization/api/organizationApi';
import {
  useDaySchedule,
  useActiveScenarios,
  useGenerateSchedule,
  useSetExclusion,
  useReorderScenarios,
} from '../hooks/useSchedule';
import { VenueDateBar } from '../components/VenueDateBar';
import { SortableScenarioList } from '../components/SortableScenarioList';
import { ScheduleBlockCard } from '../components/ScheduleBlockCard';
import { todayStr } from '../utils';

export function SchedulePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const today = todayStr();
  const [date, setDate] = useState(today);
  const [locationId, setLocationId] = useState('');

  const { data: locations } = useQuery({ queryKey: ['organization', 'locations'], queryFn: getLocations });
  useEffect(() => {
    if (locations && locations.length && !locationId) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const { data: schedule, isLoading } = useDaySchedule(locationId || undefined, date);
  const { data: scenarios } = useActiveScenarios(locationId || undefined, date);
  const generate = useGenerateSchedule(locationId, date);
  const setExclusion = useSetExclusion(locationId, date);
  const reorder = useReorderScenarios(locationId, date);

  const handleToggleExclusion = (scheduleItemId: string, excluded: boolean) => {
    setExclusion.mutate(
      { scheduleItemId, excluded },
      { onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }) },
    );
  };

  const handleGenerate = () => {
    generate.mutate(undefined, {
      onSuccess: () => toast({ status: 'success', title: t('schedule.generated'), duration: 2000 }),
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  if (locations && locations.length === 0) {
    return (
      <Box>
        <Heading size="lg" color="gray.800" mb={2}>
          {t('schedule.title')}
        </Heading>
        <Text color="gray.500" mb={4}>
          {t('schedule.noVenues')}
        </Text>
        <Button as={RouterLink} to="/organization/locations" colorScheme="brand">
          {t('schedule.addVenue')}
        </Button>
      </Box>
    );
  }

  return (
    <Box maxW="900px">
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="lg" color="gray.800">
          {t('schedule.title')}
        </Heading>
        <Button as={RouterLink} to="/schedule/report" variant="outline">
          {t('schedule.playbackReport')}
        </Button>
      </Flex>

      {locations && (
        <VenueDateBar
          locations={locations}
          locationId={locationId}
          onLocationChange={setLocationId}
          date={date}
          onDateChange={setDate}
          canGoBack={date > today}
          canGoForward
        />
      )}

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="brand.500" thickness="3px" />
        </Flex>
      ) : !schedule ? null : (
        <>
          {schedule.airRaidActive && (
            <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" px={4} py={3} mb={4}>
              <Text color="red.600" fontWeight="600">
                {t('schedule.airRaidActive')}
              </Text>
            </Box>
          )}

          {schedule.nowPlaying && (
            <Box bg="brand.50" borderRadius="md" px={4} py={3} mb={4}>
              <Text fontSize="xs" color="brand.600" textTransform="uppercase" letterSpacing="wide">
                {t('schedule.nowPlaying')}
              </Text>
              <Text fontWeight="600" color="gray.800">
                {schedule.nowPlaying.trackTitle}
                {schedule.nowPlaying.artist ? ` — ${schedule.nowPlaying.artist}` : ''}
              </Text>
            </Box>
          )}

          {!schedule.scenarioId ? (
            <Flex direction="column" align="center" py={16} textAlign="center">
              <Text fontWeight="600" color="gray.600">
                {t('schedule.noScenario')}
              </Text>
              <Button as={RouterLink} to="/scenarios" colorScheme="brand" mt={3}>
                {t('schedule.assignScenario')}
              </Button>
            </Flex>
          ) : schedule.timeBlocks.length === 0 ? (
            <Flex direction="column" align="center" py={16} textAlign="center">
              <Text fontWeight="600" color="gray.600" mb={3}>
                {t('schedule.noBlocks')}
              </Text>
              <Button colorScheme="brand" onClick={handleGenerate} isLoading={generate.isPending}>
                {t('schedule.generate')}
              </Button>
            </Flex>
          ) : (
            <>
              <VStack align="stretch" spacing={3} mb={4}>
                {schedule.timeBlocks.map((block) => (
                  <ScheduleBlockCard
                    key={block.id}
                    block={block}
                    onToggleExclusion={handleToggleExclusion}
                    busy={setExclusion.isPending}
                  />
                ))}
              </VStack>
              {!schedule.hasQueue && (
                <Button variant="outline" onClick={handleGenerate} isLoading={generate.isPending}>
                  {t('schedule.generate')}
                </Button>
              )}
            </>
          )}
        </>
      )}

      {scenarios && scenarios.length > 1 && (
        <>
          <Divider my={8} />
          <HStack justify="space-between" mb={1}>
            <Heading size="md" color="gray.700">
              {t('schedule.priority')}
            </Heading>
            {reorder.isPending && <Badge colorScheme="gray">{t('common.saving')}</Badge>}
          </HStack>
          <Text mb={3} color="gray.500" fontSize="sm">
            {t('schedule.priorityHint')}
          </Text>
          <SortableScenarioList scenarios={scenarios} onReorder={(ids) => reorder.mutate(ids)} />
        </>
      )}
    </Box>
  );
}

export default SchedulePage;
